/**
 * EducationInstitutionsList — /education/institutions
 */
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { GraduationCap, MapPin, PlusCircle, Loader2, Building2 } from "lucide-react";
import { http } from "@/lib/api";
import EducationCategoryNav from "@/components/EducationCategoryNav";

export default function EducationInstitutionsList() {
  const [params, setParams] = useSearchParams();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const setFilter = (k, v) => {
    const next = new URLSearchParams(params);
    if (v) next.set(k, v); else next.delete(k);
    setParams(next);
  };

  useEffect(() => { http.get("/education/meta").then(r => setMeta(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    setLoading(true);
    const p = Object.fromEntries([...params.entries()]);
    http.get("/education/institutions", { params: p })
      .then(r => { setItems(r.data.items || []); setTotal(r.data.total || 0); })
      .catch(() => { setItems([]); setTotal(0); })
      .finally(() => setLoading(false));
  }, [params]);

  const institution_type = params.get("institution_type") || "";
  const level    = params.get("level") || "";
  const category = params.get("category") || "";
  const country  = params.get("country") || "";
  const city     = params.get("city") || "";
  const q        = params.get("q") || "";

  return (
    <>
      <EducationCategoryNav />
      <div className="max-w-6xl mx-auto px-4 py-8" data-testid="institutions-list-page">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="overline mb-1">INSTITUTIONS</div>
            <h1 className="font-display text-4xl mb-1">
              {institution_type ? meta?.institution_types?.find(t => t.slug === institution_type)?.label + "s" : "All institutions"}
            </h1>
            <p className="opacity-70">{total} listed</p>
          </div>
          <Link to="/education/institutions/new" className="wofy-btn-primary inline-flex items-center gap-1.5" data-testid="cta-new-institution">
            <PlusCircle className="w-4 h-4" /> Register institution
          </Link>
        </div>

        <div className="wofy-card !p-3 mb-6 flex flex-wrap gap-2">
          <select value={institution_type} onChange={e => setFilter("institution_type", e.target.value)}
            className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-type">
            <option value="">All types</option>
            {meta?.institution_types?.map(t => <option key={t.slug} value={t.slug}>{t.label}</option>)}
          </select>
          <select value={level} onChange={e => setFilter("level", e.target.value)}
            className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-level">
            <option value="">Any level</option>
            {meta?.institution_levels?.map(l => <option key={l.slug} value={l.slug}>{l.label}</option>)}
          </select>
          <select value={category} onChange={e => setFilter("category", e.target.value)}
            className="text-sm px-2 py-1 rounded border" style={{ borderColor: "var(--wofy-border)" }}
            data-testid="filter-category">
            <option value="">Any field</option>
            {meta?.institution_categories?.map(c => <option key={c.slug} value={c.slug}>{c.label}</option>)}
          </select>
          <input value={country} onChange={e => setFilter("country", e.target.value)} placeholder="Country"
            className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }} />
          <input value={city} onChange={e => setFilter("city", e.target.value)} placeholder="City"
            className="text-sm px-2 py-1 rounded border w-32" style={{ borderColor: "var(--wofy-border)" }} />
          <input value={q} onChange={e => setFilter("q", e.target.value)} placeholder="Search…"
            className="text-sm px-2 py-1 rounded border flex-1 min-w-[8rem]" style={{ borderColor: "var(--wofy-border)" }} />
        </div>

        {loading ? (
          <div className="opacity-60 text-sm inline-flex items-center gap-1.5"><Loader2 className="w-4 h-4 animate-spin" />Loading…</div>
        ) : items.length === 0 ? (
          <div className="wofy-card !p-8 text-center">
            <p className="opacity-70 mb-3">No institutions match. Be the first to register.</p>
            <Link to="/education/institutions/new" className="wofy-btn-primary inline-flex items-center gap-1.5">
              <PlusCircle className="w-4 h-4" /> Register institution
            </Link>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {items.map(i => (
              <Link key={i.id} to={`/education/institutions/${i.slug}`}
                className="wofy-card !p-4 hover:shadow-md transition block"
                data-testid={`institution-card-${i.slug}`}>
                <div className="flex items-center gap-3 mb-2">
                  {i.logo_url ? (
                    <img src={i.logo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-black/5 grid place-items-center text-xs font-mono">
                      {(i.name || "?")[0].toUpperCase()}
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-medium truncate">{i.name}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider opacity-60">
                      <Building2 className="w-3 h-3 inline mr-1" />
                      {meta?.institution_types?.find(t => t.slug === i.institution_type)?.label || i.institution_type}
                    </div>
                  </div>
                </div>
                {i.description && <p className="text-sm opacity-80 line-clamp-2">{i.description}</p>}
                {(i.city || i.country) && (
                  <div className="text-xs opacity-70 flex items-center gap-1 mt-2">
                    <MapPin className="w-3 h-3" />{[i.city, i.state, i.country].filter(Boolean).join(", ")}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
